import Keycloak from 'keycloak-js'
import { runtimeConfig } from '../runtimeConfig'

/*
  Constructed on first use, not at module load.

  The realm and client id arrive from /config.json at startup, so a Keycloak built while
  this module is being imported would be built from the build-time fallbacks and then be
  wrong for every tenant that supplies a config file. Deferring to first use puts the
  construction after `loadRuntimeConfig()` has run, which `main.tsx` guarantees.
*/
let instance: Keycloak | null = null

/**
 * The Keycloak client. Authorization code + PKCE (S256), no client secret in the browser.
 */
export function getKeycloak(): Keycloak {
  if (instance === null) {
    const { keycloak: config } = runtimeConfig()

    instance = new Keycloak({
      url: config.url,
      realm: config.realm,
      clientId: config.clientId,
    })
  }

  return instance
}

/**
 * True when Keycloak is configured. With no server URL the SPA runs against the API's
 * local development bypass instead, so the whole app is usable before Keycloak exists.
 *
 * A function rather than a constant: it cannot be known until the runtime configuration
 * has loaded, and a constant evaluated at import time would answer for the build rather
 * than for the deployment.
 */
export function isKeycloakConfigured(): boolean {
  return Boolean(runtimeConfig().keycloak.url)
}

/** Parsed access-token claims we care about. */
export interface TokenClaims {
  sub?: string
  preferred_username?: string
  email?: string
  name?: string
  /**
   * Full group paths, e.g. `/orgs/acme-holdings`. The single source of truth for
   * organization membership — there is deliberately no `org_id` claim, because two
   * sources that can disagree eventually will, and the resulting 403s are undiagnosable.
   */
  groups?: string[]
  resource_access?: Record<string, { roles?: string[] }>
}

let refreshTimer: ReturnType<typeof setInterval> | undefined

/**
 * Authenticates before the app renders. Returns false when Keycloak is not configured,
 * leaving the caller to fall back to the development bypass.
 */
export async function initKeycloak(): Promise<boolean> {
  if (!isKeycloakConfigured()) {
    return false
  }

  const authenticated = await getKeycloak().init({
    onLoad: 'login-required',
    pkceMethod: 'S256',
    // A strict frame-ancestors policy forbids Keycloak's status iframe; the refresh
    // timer below covers session expiry instead.
    checkLoginIframe: false,
  })

  if (!authenticated) {
    // init() with login-required redirects rather than returning, so reaching here
    // means something is wrong with the configuration rather than the user.
    throw new Error('Keycloak did not authenticate the session.')
  }

  // Refresh well before expiry. keycloak-js no-ops when the token is still fresh.
  refreshTimer = setInterval(() => {
    void getKeycloak().updateToken(70).catch(() => {
      // A failed refresh means the session is gone. Send the user back to the login
      // page rather than leaving the SPA making 401 calls it cannot recover from.
      getKeycloak().login()
    })
  }, 60_000)

  return true
}

export function stopTokenRefresh(): void {
  if (refreshTimer) {
    clearInterval(refreshTimer)
    refreshTimer = undefined
  }
}

/** A token guaranteed fresh for at least `minValiditySeconds`. */
export async function getFreshToken(minValiditySeconds = 30): Promise<string | undefined> {
  if (!isKeycloakConfigured()) {
    return undefined
  }

  try {
    await getKeycloak().updateToken(minValiditySeconds)
  } catch {
    getKeycloak().login()
    return undefined
  }

  return getKeycloak().token
}

export function logout(): void {
  stopTokenRefresh()
  if (isKeycloakConfigured()) {
    void getKeycloak().logout({ redirectUri: window.location.origin })
  }
}

export function claims(): TokenClaims {
  return (getKeycloak().tokenParsed ?? {}) as TokenClaims
}
